import type { McpOAuthGrant } from '../../types/api'

export default function OAuthGrantsSection({
  grants,
  isLoading,
  isRevokingId,
  onRevoke,
}: {
  /** Null when the server has MCP OAuth disabled (bearer-only mode). */
  grants: McpOAuthGrant[] | null
  isLoading: boolean
  isRevokingId?: number | null
  onRevoke: (id: number) => void
}) {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-harbor-text">OAuth grants</h2>
        <p className="text-sm text-harbor-text-secondary">
          One-click Claude connectors (no API-key paste). Grants are audience-bound to this
          deployment&apos;s /mcp resource — revoking stops the client immediately.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-emerald-500" />
        </div>
      ) : grants === null ? (
        <p className="text-sm text-harbor-text-muted">
          URL-only Claude connectors are unavailable until an admin enables MCP OAuth on the server
          (MCP_OAUTH_ENABLED + Hydra). Bearer API keys keep working.
        </p>
      ) : grants.length === 0 ? (
        <p className="text-sm text-harbor-text-muted">
          No OAuth grants yet. Add this server by URL in Claude to create one.
        </p>
      ) : (
        <ul className="space-y-3">
          {grants.map((grant) => {
            const revoked = grant.revoked_at !== null
            const revoking = isRevokingId === grant.id
            return (
              <li
                key={grant.id}
                className="rounded-lg border border-harbor-border bg-harbor-surface-raised p-4"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-sm text-harbor-text">{grant.client_id}</p>
                    <p className="truncate text-xs text-harbor-text-secondary">
                      {grant.user_email} · {grant.scope || 'mcp:read'}
                    </p>
                    <p className="truncate text-xs text-harbor-text-muted">{grant.audience}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    {revoked ? (
                      <span className="text-xs font-medium text-harbor-text-muted">Revoked</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onRevoke(grant.id)}
                        disabled={revoking}
                        className="rounded-lg bg-red-900/40 px-3 py-1.5 text-xs font-medium text-red-200 transition-colors hover:bg-red-900/60 disabled:opacity-50"
                      >
                        {revoking ? 'Revoking…' : 'Revoke'}
                      </button>
                    )}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
